import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { CancellationActorLabel, ScheduleListRow } from './types';
import type { MomentsSelectedPairAppointmentPage } from '@/features/appointments/momentsSelectedPairContract';
import type {
  DecodedBrowserAppointment,
  PlatformAdminAppointment,
  PlatformAdminCancellationEvidence,
} from '@/features/appointments/types';
import type { Session } from '@/types';

import {
  actionRecordSessionSubject,
  isNativeAppointmentActionRecord,
  reconcileAppointmentActionRecords,
  reconcileCalendarAppointmentActionRecords,
} from '@/features/appointments/appointmentActionRecord';
import {
  type CalendarAppointmentActionRecord,
  platformAdminCalendarAppointmentRecord,
} from '@/features/appointments/calendarAppointmentProjection';
import { legacyAppSelectedPair } from '@/features/appointments/momentsSelectedPairContract';
import { NON_CANCELLED_SESSION_STATUSES } from '@/features/coachSchedule/HideCancelledToggle';
import { dayKeyFromIso } from '@/features/coachSchedule/calendarGrid';
import { flattenSessionGroups } from '@/features/coachSchedule/sessionData';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useGetAppointmentsQuery } from '@/store/api/appointmentApi';
import {
  useGetMomentsSelectedPairAppointmentsQuery,
  useGetMomentsSelectedPairCancellationEvidenceQuery,
} from '@/store/api/momentsAppointmentApi';
import { useGetSessionsByCoachQuery } from '@/store/api/sessionApi';

const PAGE_SIZE = 15;
const EMPTY_CANCELLATION_EVIDENCE: PlatformAdminCancellationEvidence[] = [];

interface SessionPage {
  items: Record<string, Session[]>;
  pages?: number;
  total?: number;
}

interface SessionStreams {
  futureByPage: Record<number, Session[]>;
  futurePages: number;
  pastByPage: Record<number, Session[]>;
  pastPages: number;
  scopeKey: string;
}

interface EvidencePages {
  byCursor: Record<string, PlatformAdminCancellationEvidence[]>;
  observedAt: string | null;
  paginationError: boolean;
  scopeKey: string;
}

interface AdminAppointmentPages {
  byCursor: Record<string, PlatformAdminAppointment[]>;
  paginationError: boolean;
  scopeKey: string;
}

interface EvidenceCursor {
  scopeKey: string;
  value: string;
}

interface AdminAppointmentCursor {
  scopeKey: string;
  value: string;
}

const FIRST_EVIDENCE_PAGE = 'first';
const FIRST_ADMIN_APPOINTMENT_PAGE = 'first';
const emptyEvidencePages = (scopeKey: string): EvidencePages => ({
  byCursor: {},
  observedAt: null,
  paginationError: false,
  scopeKey,
});

const emptyAdminAppointmentPages = (
  scopeKey: string,
): AdminAppointmentPages => ({
  byCursor: {},
  paginationError: false,
  scopeKey,
});

const appointmentIdentity = (appointment: PlatformAdminAppointment) =>
  `${appointment.id}:${appointment.version}`;

const flattenAdminAppointmentPages = (
  pages: Record<string, PlatformAdminAppointment[]>,
): PlatformAdminAppointment[] => {
  const order: string[] = [];
  const latest = new Map<string, PlatformAdminAppointment>();
  Object.values(pages)
    .flat()
    .forEach((appointment) => {
      const identity = appointmentIdentity(appointment);
      if (!latest.has(identity)) order.push(identity);
      latest.set(identity, appointment);
    });
  return order.flatMap((identity) => {
    const appointment = latest.get(identity);
    return appointment ? [appointment] : [];
  });
};

const evidenceIdentity = (evidence: PlatformAdminCancellationEvidence) =>
  evidence.operationId
    ? `operation:${evidence.operationId}`
    : `history:${evidence.historyId ?? evidence.requestedAt}`;

const flattenEvidencePages = (
  pages: Record<string, PlatformAdminCancellationEvidence[]>,
): PlatformAdminCancellationEvidence[] => {
  const order: string[] = [];
  const latest = new Map<string, PlatformAdminCancellationEvidence>();
  Object.values(pages)
    .flat()
    .forEach((evidence) => {
      const identity = evidenceIdentity(evidence);
      if (!latest.has(identity)) order.push(identity);
      latest.set(identity, evidence);
    });
  return order.flatMap((identity) => {
    const evidence = latest.get(identity);
    return evidence ? [evidence] : [];
  });
};

const emptyStreams = (scopeKey: string): SessionStreams => ({
  futureByPage: {},
  futurePages: 1,
  pastByPage: {},
  pastPages: 1,
  scopeKey,
});

const dedupeSessions = (sessions: Session[]): Session[] => {
  const seen = new Set<string>();
  return sessions.filter((session) => {
    if (seen.has(session.id)) return false;
    seen.add(session.id);
    return true;
  });
};

const flattenSessionPages = (pages: Record<number, Session[]>): Session[] =>
  dedupeSessions(
    Object.entries(pages)
      .sort(([left], [right]) => Number(left) - Number(right))
      .flatMap(([, sessions]) => sessions),
  );

const pageCount = (data: SessionPage, sessions: Session[]): number =>
  data.pages ??
  Math.max(1, Math.ceil((data.total ?? sessions.length) / PAGE_SIZE));

const firstPageSessions = (
  page: number,
  data: SessionPage | undefined,
  include: (session: Session) => boolean,
): Session[] => {
  if (page !== 1 || !data) return [];
  return flattenSessionGroups(data.items).filter(include);
};

const sourceIsLoading = (
  data: unknown,
  loadedCount: number,
  isError?: boolean,
): boolean => !data && loadedCount === 0 && !isError;

const listStatus = (
  hasError: boolean,
  isInitialLoading: boolean,
): 'loading' | 'ready' | 'error' => {
  if (hasError) return 'error';
  if (isInitialLoading) return 'loading';
  return 'ready';
};

const startOfDayIso = (dateKey: string, timeZone: string): string => {
  const [year = 0, month = 1, day = 1] = dateKey.split('-').map(Number);
  const target = Date.UTC(year, month - 1, day);
  const formatter = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone,
    year: 'numeric',
  });
  let instant = target;

  for (let pass = 0; pass < 2; pass += 1) {
    const parts = formatter.formatToParts(new Date(instant));
    const value = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((part) => part.type === type)?.value ?? 0);
    const represented = Date.UTC(
      value('year'),
      value('month') - 1,
      value('day'),
      value('hour'),
      value('minute'),
      value('second'),
    );
    instant += target - represented;
  }

  const dateKeyAt = (timestamp: number) =>
    dayKeyFromIso(new Date(timestamp).toISOString(), timeZone);
  const hour = 60 * 60 * 1000;
  let lower: number;
  let upper: number;

  if (dateKeyAt(instant) < dateKey) {
    lower = instant;
    upper = instant + hour;
    while (dateKeyAt(upper) < dateKey) upper += hour;
  } else {
    upper = instant;
    lower = instant - hour;
    while (dateKeyAt(lower) >= dateKey) lower -= hour;
  }

  while (upper - lower > 1) {
    const midpoint = Math.floor((lower + upper) / 2);
    if (dateKeyAt(midpoint) < dateKey) lower = midpoint;
    else upper = midpoint;
  }

  return new Date(upper).toISOString();
};

const actorLabel = (role: string | null): CancellationActorLabel | null => {
  if (role === 'member') return 'Member';
  if (role === 'coach') return 'Coach';
  if (role === 'admin') return 'Ops';
  return null;
};

const composeRows = (
  appointments: DecodedBrowserAppointment[],
  sessions: Session[],
  evidence: PlatformAdminCancellationEvidence[],
  adminAppointments?: PlatformAdminAppointment[],
  hideCancelled = false,
): { rows: ScheduleListRow[] } => {
  const rows: ScheduleListRow[] = [];
  const records = adminAppointments
    ? reconcileCalendarAppointmentActionRecords(
        adminAppointments.map(platformAdminCalendarAppointmentRecord),
        sessions,
      )
    : reconcileAppointmentActionRecords(appointments, sessions);
  const evidenceByAppointment = new Map<
    string,
    PlatformAdminCancellationEvidence[]
  >();
  evidence.forEach((item) => {
    item.appointmentIds.forEach((appointmentId) => {
      const indexed = evidenceByAppointment.get(appointmentId) ?? [];
      indexed.push(item);
      evidenceByAppointment.set(appointmentId, indexed);
    });
  });

  records.forEach((record) => {
    if (!isNativeAppointmentActionRecord(record)) {
      if (hideCancelled && record.status === 'cancelled') return;
      rows.push({
        key: `session:${record.id}`,
        session: record,
        source: 'rails-session',
      });
      return;
    }
    if (
      (hideCancelled || record.source === 'participant-appointment') &&
      record.view.lifecycleStatus === 'cancelled'
    ) {
      return;
    }

    const cancellationEvidence =
      evidenceByAppointment.get(record.view.id) ?? [];
    const actionRecord: CalendarAppointmentActionRecord = record;
    const actionSession = actionRecordSessionSubject(record);
    const displaySession = record.railsSession
      ? {
          ...actionSession,
          user: {
            ...actionSession.user,
            avatarUrl: record.railsSession.user.avatarUrl,
            cometChatId: record.railsSession.user.cometChatId,
            company: record.railsSession.user.company,
            id: record.railsSession.user.id,
          },
        }
      : actionSession;
    const primaryEvidence = cancellationEvidence[0];
    const label = primaryEvidence
      ? actorLabel(primaryEvidence.actor.role)
      : null;
    const common = {
      actionRecord,
      cancellation:
        record.view.cancellation && label && primaryEvidence
          ? { actorLabel: label, proximity: primaryEvidence.proximity }
          : null,
      cancellationEvidence,
      displaySession,
      key: `appointment:${record.view.id}` as const,
      ...(record.railsSession ? { pairedSession: record.railsSession } : {}),
    };
    if (record.source === 'platform-admin-appointment') {
      rows.push({ ...common, source: 'platform-admin-appointment' });
      return;
    }
    rows.push({
      ...common,
      appointment: record.authoritative,
      source: 'moments-appointment',
    });
  });

  return { rows };
};

export function usePlatformScheduleList(input: {
  adminCoachId?: string;
  coachId: string;
  hideCancelled?: boolean;
  memberId: string;
  timeZone: string;
}): {
  cancellationEvidence: PlatformAdminCancellationEvidence[];
  rows: ScheduleListRow[];
  status: 'loading' | 'ready' | 'error';
  loadMorePast(): void;
  loadMoreFuture(): void;
  hasMorePast: boolean;
  hasMoreFuture: boolean;
  hasMoreEvidence: boolean;
  evidenceLoadError: boolean;
  evidenceUnavailable: boolean;
  isLoadingMoreEvidence: boolean;
  loadMoreEvidence(): void;
  refetch(): void;
} {
  const {
    adminCoachId,
    coachId,
    hideCancelled = false,
    memberId,
    timeZone,
  } = input;
  // MEN-5349: filter on the server so Session pages stay full.
  const statuses = hideCancelled ? NON_CANCELLED_SESSION_STATUSES : undefined;
  const hasAdminEvidenceAuthority = Boolean(adminCoachId);
  const actingRole = hasAdminEvidenceAuthority ? undefined : ('coach' as const);
  const { user: currentUser } = useCurrentUser();
  const [futurePage, setFuturePage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const [evidenceCursor, setEvidenceCursor] = useState<EvidenceCursor | null>(
    null,
  );
  const [adminAppointmentCursor, setAdminAppointmentCursor] =
    useState<AdminAppointmentCursor | null>(null);
  const [refreshFirstPages, setRefreshFirstPages] = useState(false);
  const [refreshFirstEvidence, setRefreshFirstEvidence] = useState(false);
  const anchor = useMemo(
    () => dayKeyFromIso(new Date().toISOString(), timeZone),
    [timeZone],
  );
  const queryBoundary = useMemo(
    () => startOfDayIso(anchor, timeZone),
    [anchor, timeZone],
  );
  const adminRange = useMemo(() => {
    const boundary = Date.parse(queryBoundary);
    const windowMillis = 180 * 24 * 60 * 60 * 1000;
    return {
      rangeEnd: new Date(boundary + windowMillis).toISOString(),
      rangeStart: new Date(boundary - windowMillis).toISOString(),
    };
  }, [queryBoundary]);
  const scopeKey = `${currentUser.id}:${adminCoachId ?? 'coach'}:${coachId}:${memberId}:${queryBoundary}:${hideCancelled ? 'hide' : 'show'}-cancelled`;
  const [streams, setStreams] = useState<SessionStreams>(() =>
    emptyStreams(scopeKey),
  );
  const [evidencePages, setEvidencePages] = useState<EvidencePages>(() =>
    emptyEvidencePages(scopeKey),
  );
  const [adminAppointmentPages, setAdminAppointmentPages] =
    useState<AdminAppointmentPages>(() => emptyAdminAppointmentPages(scopeKey));

  const future = useGetSessionsByCoachQuery({
    ...(actingRole ? { actingRole } : {}),
    coachId,
    limit: PAGE_SIZE,
    minDate: queryBoundary,
    order: 'asc',
    page: futurePage,
    statuses,
    userId: memberId,
    viewerId: currentUser.id,
  });
  const past = useGetSessionsByCoachQuery({
    ...(actingRole ? { actingRole } : {}),
    coachId,
    limit: PAGE_SIZE,
    maxDate: queryBoundary,
    order: 'desc',
    page: pastPage,
    statuses,
    userId: memberId,
    viewerId: currentUser.id,
  });
  const appointments = useGetAppointmentsQuery(
    adminCoachId
      ? skipToken
      : {
          coachId,
          memberId,
          userId: currentUser.id,
        },
  );
  const selectedPair = useMemo(
    () => legacyAppSelectedPair(coachId, memberId),
    [coachId, memberId],
  );
  const activeAdminAppointmentCursor =
    adminAppointmentCursor?.scopeKey === scopeKey
      ? adminAppointmentCursor.value
      : undefined;
  const firstAdminAppointmentsQuery =
    useGetMomentsSelectedPairAppointmentsQuery(
      {
        ...selectedPair,
        limit: PAGE_SIZE,
        ...adminRange,
      },
      { skip: !adminCoachId },
    );
  const cursorAdminAppointmentsQuery =
    useGetMomentsSelectedPairAppointmentsQuery(
      {
        ...selectedPair,
        ...(activeAdminAppointmentCursor
          ? { cursor: activeAdminAppointmentCursor }
          : {}),
        limit: PAGE_SIZE,
        ...adminRange,
      },
      { skip: !adminCoachId || !activeAdminAppointmentCursor },
    );
  const adminAppointments = activeAdminAppointmentCursor
    ? cursorAdminAppointmentsQuery
    : firstAdminAppointmentsQuery;
  const activeEvidenceCursor =
    evidenceCursor?.scopeKey === scopeKey ? evidenceCursor.value : undefined;
  const firstEvidenceQuery = useGetMomentsSelectedPairCancellationEvidenceQuery(
    {
      ...selectedPair,
      limit: PAGE_SIZE,
    },
    { skip: !hasAdminEvidenceAuthority },
  );
  const cursorEvidenceQuery =
    useGetMomentsSelectedPairCancellationEvidenceQuery(
      {
        ...selectedPair,
        ...(activeEvidenceCursor ? { cursor: activeEvidenceCursor } : {}),
        limit: PAGE_SIZE,
      },
      { skip: !hasAdminEvidenceAuthority || !activeEvidenceCursor },
    );
  const evidenceQuery = activeEvidenceCursor
    ? cursorEvidenceQuery
    : firstEvidenceQuery;
  const activeEvidenceCursorRef = useRef(activeEvidenceCursor);
  activeEvidenceCursorRef.current = activeEvidenceCursor;
  const activeAdminAppointmentCursorRef = useRef(activeAdminAppointmentCursor);
  activeAdminAppointmentCursorRef.current = activeAdminAppointmentCursor;

  useEffect(() => {
    setFuturePage(1);
    setPastPage(1);
    setEvidenceCursor(null);
    setAdminAppointmentCursor(null);
    setStreams(emptyStreams(scopeKey));
    setEvidencePages(emptyEvidencePages(scopeKey));
    setAdminAppointmentPages(emptyAdminAppointmentPages(scopeKey));
  }, [scopeKey]);

  useEffect(() => {
    if (!adminCoachId) return;
    const page = firstAdminAppointmentsQuery.currentData;
    if (!page) return;
    const shouldRebase = activeAdminAppointmentCursorRef.current !== undefined;
    setAdminAppointmentPages((current) => {
      const base =
        current.scopeKey === scopeKey
          ? current
          : emptyAdminAppointmentPages(scopeKey);
      const paginationError =
        !shouldRebase &&
        Boolean(
          page.nextCursor &&
          page.nextCursor in base.byCursor &&
          page.nextCursor !== FIRST_ADMIN_APPOINTMENT_PAGE,
        );
      return {
        ...(shouldRebase ? emptyAdminAppointmentPages(scopeKey) : base),
        byCursor: {
          ...(shouldRebase ? {} : base.byCursor),
          [FIRST_ADMIN_APPOINTMENT_PAGE]: page.appointments,
        },
        paginationError,
      };
    });
    if (shouldRebase) setAdminAppointmentCursor(null);
  }, [adminCoachId, firstAdminAppointmentsQuery.currentData, scopeKey]);

  useEffect(() => {
    if (!adminCoachId || !activeAdminAppointmentCursor) return;
    const page = cursorAdminAppointmentsQuery.currentData;
    if (!page) return;
    setAdminAppointmentPages((current) => {
      const base =
        current.scopeKey === scopeKey
          ? current
          : emptyAdminAppointmentPages(scopeKey);
      const paginationError = Boolean(
        page.nextCursor &&
        (page.nextCursor === activeAdminAppointmentCursor ||
          page.nextCursor in base.byCursor),
      );
      return {
        ...base,
        byCursor: {
          ...base.byCursor,
          [activeAdminAppointmentCursor]: page.appointments,
        },
        paginationError,
      };
    });
  }, [
    activeAdminAppointmentCursor,
    adminCoachId,
    cursorAdminAppointmentsQuery.currentData,
    scopeKey,
  ]);

  useEffect(() => {
    if (!hasAdminEvidenceAuthority) return;
    const page = firstEvidenceQuery.currentData;
    if (!page) return;
    const shouldRebase = activeEvidenceCursorRef.current !== undefined;
    setEvidencePages((current) => {
      const base =
        current.scopeKey === scopeKey ? current : emptyEvidencePages(scopeKey);
      const paginationError =
        !shouldRebase &&
        Boolean(
          page.nextCursor &&
          page.nextCursor in base.byCursor &&
          page.nextCursor !== FIRST_EVIDENCE_PAGE,
        );
      return {
        ...(shouldRebase ? emptyEvidencePages(scopeKey) : base),
        byCursor: {
          ...(shouldRebase ? {} : base.byCursor),
          [FIRST_EVIDENCE_PAGE]: page.evidence,
        },
        observedAt: page.observedAt,
        paginationError,
      };
    });
    if (shouldRebase) setEvidenceCursor(null);
  }, [firstEvidenceQuery.currentData, hasAdminEvidenceAuthority, scopeKey]);

  useEffect(() => {
    if (!hasAdminEvidenceAuthority || !activeEvidenceCursor) return;
    const page = cursorEvidenceQuery.currentData;
    if (!page) return;
    setEvidencePages((current) => {
      const base =
        current.scopeKey === scopeKey ? current : emptyEvidencePages(scopeKey);
      if (base.observedAt && base.observedAt !== page.observedAt) {
        return { ...base, paginationError: true };
      }
      const paginationError = Boolean(
        page.nextCursor &&
        (page.nextCursor === activeEvidenceCursor ||
          page.nextCursor in base.byCursor),
      );
      return {
        ...base,
        byCursor: {
          ...base.byCursor,
          [activeEvidenceCursor]: page.evidence,
        },
        observedAt: base.observedAt ?? page.observedAt,
        paginationError,
      };
    });
  }, [
    activeEvidenceCursor,
    cursorEvidenceQuery.currentData,
    hasAdminEvidenceAuthority,
    scopeKey,
  ]);

  const futureData = future.currentData;
  const pastData = past.currentData;
  const refetchFuture = future.refetch;
  const refetchPast = past.refetch;
  const refetchEvidence = firstEvidenceQuery.refetch;

  useEffect(() => {
    if (futurePage === 1 || !futureData || !future.isFetching) return;
    setFuturePage(1);
    setStreams((current) =>
      current.scopeKey === scopeKey
        ? { ...current, futureByPage: {} }
        : current,
    );
  }, [future.isFetching, futureData, futurePage, scopeKey]);

  useEffect(() => {
    if (pastPage === 1 || !pastData || !past.isFetching) return;
    setPastPage(1);
    setStreams((current) =>
      current.scopeKey === scopeKey ? { ...current, pastByPage: {} } : current,
    );
  }, [past.isFetching, pastData, pastPage, scopeKey]);

  useEffect(() => {
    if (!refreshFirstPages || futurePage !== 1 || pastPage !== 1) return;
    setRefreshFirstPages(false);
    refetchFuture();
    refetchPast();
  }, [futurePage, pastPage, refetchFuture, refetchPast, refreshFirstPages]);

  useEffect(() => {
    if (!hasAdminEvidenceAuthority) return;
    if (!refreshFirstEvidence || activeEvidenceCursor !== undefined) return;
    setRefreshFirstEvidence(false);
    refetchEvidence();
  }, [
    activeEvidenceCursor,
    hasAdminEvidenceAuthority,
    refetchEvidence,
    refreshFirstEvidence,
  ]);

  useEffect(() => {
    if (!futureData) return;
    const pageSessions = flattenSessionGroups(futureData.items).filter(
      (session) => dayKeyFromIso(session.startedAt, timeZone) >= anchor,
    );
    setStreams((current) => {
      const base =
        current.scopeKey === scopeKey ? current : emptyStreams(scopeKey);
      return {
        ...base,
        futureByPage: {
          ...base.futureByPage,
          [futurePage]: pageSessions,
        },
        futurePages: pageCount(futureData, pageSessions),
      };
    });
  }, [anchor, futureData, futurePage, scopeKey, timeZone]);

  useEffect(() => {
    if (!pastData) return;
    const pageSessions = flattenSessionGroups(pastData.items).filter(
      (session) => dayKeyFromIso(session.startedAt, timeZone) < anchor,
    );
    setStreams((current) => {
      const base =
        current.scopeKey === scopeKey ? current : emptyStreams(scopeKey);
      return {
        ...base,
        pastByPage: {
          ...base.pastByPage,
          [pastPage]: pageSessions,
        },
        pastPages: pageCount(pastData, pageSessions),
      };
    });
  }, [anchor, pastData, pastPage, scopeKey, timeZone]);

  const scopedStreams =
    streams.scopeKey === scopeKey ? streams : emptyStreams(scopeKey);
  const firstFuturePage = firstPageSessions(
    futurePage,
    futureData,
    (session) => dayKeyFromIso(session.startedAt, timeZone) >= anchor,
  );
  const firstPastPage = firstPageSessions(
    pastPage,
    pastData,
    (session) => dayKeyFromIso(session.startedAt, timeZone) < anchor,
  );
  const retainedFutureSessions = useMemo(
    () => flattenSessionPages(scopedStreams.futureByPage),
    [scopedStreams.futureByPage],
  );
  const retainedPastSessions = useMemo(
    () => flattenSessionPages(scopedStreams.pastByPage),
    [scopedStreams.pastByPage],
  );
  const futureSessions =
    retainedFutureSessions.length > 0
      ? retainedFutureSessions
      : firstFuturePage;
  const pastSessions =
    retainedPastSessions.length > 0 ? retainedPastSessions : firstPastPage;
  const appointmentData = appointments.currentData;
  const scopedAdminAppointmentPages =
    adminAppointmentPages.scopeKey === scopeKey
      ? adminAppointmentPages
      : emptyAdminAppointmentPages(scopeKey);
  const retainedAdminAppointments = useMemo(
    () => flattenAdminAppointmentPages(scopedAdminAppointmentPages.byCursor),
    [scopedAdminAppointmentPages.byCursor],
  );
  const firstAdminAppointments =
    firstAdminAppointmentsQuery.currentData?.appointments ?? [];
  const adminAppointmentData: MomentsSelectedPairAppointmentPage | undefined =
    adminCoachId &&
    (retainedAdminAppointments.length > 0 ||
      firstAdminAppointmentsQuery.currentData)
      ? {
          appointments:
            retainedAdminAppointments.length > 0
              ? retainedAdminAppointments
              : firstAdminAppointments,
          contractVersion: 'appointment-platform-selected-pair-page.v1',
          nextCursor: adminAppointments.currentData?.nextCursor ?? null,
        }
      : undefined;
  const scopedEvidencePages =
    evidencePages.scopeKey === scopeKey
      ? evidencePages
      : emptyEvidencePages(scopeKey);
  const retainedEvidence = useMemo(
    () => flattenEvidencePages(scopedEvidencePages.byCursor),
    [scopedEvidencePages.byCursor],
  );
  const firstEvidence =
    firstEvidenceQuery.currentData?.evidence ?? EMPTY_CANCELLATION_EVIDENCE;
  let cancellationEvidence = EMPTY_CANCELLATION_EVIDENCE;
  const evidenceUnavailable = Boolean(
    hasAdminEvidenceAuthority && firstEvidenceQuery.isError,
  );
  const evidenceRevalidating = Boolean(
    hasAdminEvidenceAuthority && firstEvidenceQuery.isFetching,
  );
  if (
    hasAdminEvidenceAuthority &&
    !evidenceUnavailable &&
    !evidenceRevalidating
  ) {
    cancellationEvidence =
      retainedEvidence.length > 0 ? retainedEvidence : firstEvidence;
  }

  const composition = useMemo(
    () =>
      appointmentData || adminAppointmentData
        ? composeRows(
            appointmentData?.appointments ?? [],
            [...pastSessions, ...futureSessions],
            cancellationEvidence,
            adminCoachId ? adminAppointmentData?.appointments : undefined,
            hideCancelled,
          )
        : { rows: [] },
    [
      adminAppointmentData,
      adminCoachId,
      appointmentData,
      cancellationEvidence,
      futureSessions,
      hideCancelled,
      pastSessions,
    ],
  );
  const evidenceLoadError = Boolean(
    hasAdminEvidenceAuthority &&
    ((cursorEvidenceQuery.isError && retainedEvidence.length > 0) ||
      scopedEvidencePages.paginationError),
  );
  const hasError = [
    Boolean(!adminCoachId && appointments.isError),
    Boolean(
      adminCoachId &&
      (adminAppointments.isError ||
        scopedAdminAppointmentPages.paginationError),
    ),
    future.isError,
    past.isError,
  ].some(Boolean);
  const isInitialLoading = [
    Boolean(
      !adminCoachId &&
      sourceIsLoading(appointments.currentData, 0, appointments.isError),
    ),
    Boolean(
      adminCoachId &&
      sourceIsLoading(
        adminAppointmentData,
        retainedAdminAppointments.length,
        adminAppointments.isError,
      ),
    ),
    sourceIsLoading(futureData, retainedFutureSessions.length, future.isError),
    sourceIsLoading(pastData, retainedPastSessions.length, past.isError),
  ].some(Boolean);
  const status = listStatus(hasError, isInitialLoading);
  const hasMorePast = pastPage < scopedStreams.pastPages;
  const hasMoreAdminAppointments = Boolean(
    adminCoachId &&
    !adminAppointments.isError &&
    !scopedAdminAppointmentPages.paginationError &&
    adminAppointments.currentData?.nextCursor &&
    !(
      adminAppointments.currentData.nextCursor in
      scopedAdminAppointmentPages.byCursor
    ),
  );
  const hasMoreFuture =
    futurePage < scopedStreams.futurePages || hasMoreAdminAppointments;
  const hasMoreEvidence = Boolean(
    hasAdminEvidenceAuthority &&
    !cursorEvidenceQuery.isError &&
    !scopedEvidencePages.paginationError &&
    evidenceQuery.currentData?.nextCursor &&
    !(evidenceQuery.currentData.nextCursor in scopedEvidencePages.byCursor),
  );

  return {
    cancellationEvidence: status === 'ready' ? cancellationEvidence : [],
    hasMoreFuture,
    hasMoreEvidence,
    hasMorePast,
    evidenceLoadError,
    evidenceUnavailable,
    isLoadingMoreEvidence: Boolean(
      hasAdminEvidenceAuthority &&
      activeEvidenceCursor &&
      evidenceQuery.isFetching,
    ),
    loadMoreEvidence() {
      if (!hasAdminEvidenceAuthority) return;
      const nextCursor = evidenceQuery.currentData?.nextCursor;
      if (!nextCursor || evidenceQuery.isFetching) return;
      setEvidenceCursor({ scopeKey, value: nextCursor });
    },
    loadMoreFuture() {
      if (hasMoreAdminAppointments && !adminAppointments.isFetching) {
        const nextCursor = adminAppointments.currentData?.nextCursor;
        if (nextCursor)
          setAdminAppointmentCursor({ scopeKey, value: nextCursor });
      }
      if (futurePage >= scopedStreams.futurePages || future.isFetching) return;
      setFuturePage((page) => Math.min(page + 1, scopedStreams.futurePages));
    },
    loadMorePast() {
      if (!hasMorePast || past.isFetching) return;
      setPastPage((page) => Math.min(page + 1, scopedStreams.pastPages));
    },
    refetch() {
      setFuturePage(1);
      setPastPage(1);
      setStreams(emptyStreams(scopeKey));
      setEvidenceCursor(null);
      setAdminAppointmentCursor(null);
      setEvidencePages(emptyEvidencePages(scopeKey));
      setAdminAppointmentPages(emptyAdminAppointmentPages(scopeKey));
      setRefreshFirstPages(true);
      if (hasAdminEvidenceAuthority) {
        setRefreshFirstEvidence(true);
      }
      if (adminCoachId) firstAdminAppointmentsQuery.refetch();
      else appointments.refetch();
    },
    rows: status === 'ready' ? composition.rows : [],
    status,
  };
}
